import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, ArrowRight, ArrowsClockwise } from "@phosphor-icons/react";

const C = { bg: "#000", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", red: "#F87171", green: "#9AE6B4" };

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reference = searchParams.get("reference") || searchParams.get("trxref");
  const [state, setState] = useState("verifying"); // verifying | success | failed | pending
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!reference) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 5;

    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/credits/verify/${reference}`);
        if (cancelled) return;
        setDetails(data);
        if (data.status === "success") {
          setState("success");
          return;
        }
        if (["failed", "abandoned", "reversed"].includes(data.status)) {
          setState("failed");
          return;
        }
        // pending — retry up to maxAttempts
        if (attempts < maxAttempts) {
          setTimeout(tick, 2000);
        } else {
          setState("pending");
        }
      } catch (e) {
        if (cancelled) return;
        if (attempts < maxAttempts) {
          setTimeout(tick, 2000);
        } else {
          setState("failed");
        }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [reference]);

  return (
    <div className="min-h-screen grid place-items-center text-white px-6" style={{ backgroundColor: C.bg }} data-testid="payment-callback">
      <div className="max-w-md w-full rounded-2xl border p-8 text-center"
        style={{ borderColor: "rgba(123, 211, 206, 0.18)", backgroundColor: "#0A2826" }}>
        {state === "verifying" && (
          <>
            <ArrowsClockwise size={48} weight="bold" className="mx-auto animate-spin" style={{ color: C.tealSoft }} />
            <h1 className="font-display font-black text-2xl mt-5" style={{ letterSpacing: "-0.02em" }}>Verifying payment…</h1>
            <p className="text-white/55 mt-2 text-sm">This usually takes a few seconds. Don&apos;t close this tab.</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle size={56} weight="fill" className="mx-auto" style={{ color: C.green }} />
            <h1 className="font-display font-black text-3xl mt-5" style={{ letterSpacing: "-0.02em" }}>Payment received.</h1>
            <p className="text-white/70 mt-3 leading-relaxed">
              <span className="font-bold text-white">+{details?.credits_added || 0} credits</span> just landed in your account.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-[12px] font-mono-num rounded-full px-3 py-1.5"
              style={{ backgroundColor: `${C.orange}15`, border: `1px solid ${C.orange}40`, color: C.orange }}>
              New balance:&nbsp;<span className="font-extrabold">{details?.balance ?? "—"}</span>&nbsp;credits
            </div>
            <button onClick={() => navigate("/dashboard")} data-testid="payment-back-to-dashboard"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold transition-colors"
              style={{ backgroundColor: C.orange, color: "#000" }}>
              Back to building <ArrowRight size={14} weight="bold" />
            </button>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle size={56} weight="fill" className="mx-auto" style={{ color: C.red }} />
            <h1 className="font-display font-black text-2xl mt-5">Payment didn&apos;t go through</h1>
            <p className="text-white/55 mt-3 text-sm leading-relaxed">
              {details?.status === "abandoned"
                ? "Looks like the payment was cancelled. No charge was made."
                : "We couldn't confirm your payment. If you were charged, credits will appear automatically once Paystack settles the transaction — usually within a minute."}
            </p>
            <Link to="/credits" data-testid="payment-try-again"
              className="mt-6 inline-block w-full px-5 py-3 rounded-md text-sm font-extrabold"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              Try again
            </Link>
          </>
        )}

        {state === "pending" && (
          <>
            <ArrowsClockwise size={48} weight="bold" className="mx-auto" style={{ color: C.orange }} />
            <h1 className="font-display font-black text-2xl mt-5">Still processing</h1>
            <p className="text-white/65 mt-3 text-sm leading-relaxed">
              Your bank is taking a little longer than usual. Credits will appear automatically once
              Paystack confirms — no action needed from you.
            </p>
            <button onClick={() => navigate("/dashboard")} data-testid="payment-back-pending"
              className="mt-6 w-full px-5 py-3 rounded-md text-sm font-extrabold"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              Back to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
